import { parseWorkspaceHost } from "@slate/shared";

/**
 * Runtime workspace resolution (docs/decisions.md #22) - how one static
 * build serves every tenant subdomain. On <slug>.obviouscrm.com the app
 * asks the control plane which Supabase project this workspace uses; the
 * response carries only public-by-design values (project URL + publishable
 * key). Build-time env vars remain as the fallback, which is exactly the
 * current single-tenant deployment - so nothing changes there until the
 * control plane endpoint is live.
 */

export interface WorkspaceRuntimeConfig {
  supabaseUrl: string;
  publishableKey: string;
  slug?: string;
}

/** Thrown when the subdomain is well-formed but no workspace exists. */
export class WorkspaceNotFoundError extends Error {
  constructor(public slug: string) {
    super(`No workspace registered for "${slug}"`);
  }
}

/**
 * Thrown on the hq subdomain: that address belongs to the Grey Matter
 * console, which is its own deployment - the tenant app must never serve a
 * workspace there, even via the env fallback.
 */
export class ReservedHostError extends Error {}

/**
 * Thrown on the bare/base domain (obviouscrm.com, www.obviouscrm.com): there
 * is no workspace there - it's not a tenant subdomain and not hq - so the
 * app must never attempt Supabase auth against the env-var fallback.
 */
export class ApexHostError extends Error {}

const envConfig = (): WorkspaceRuntimeConfig | null => {
  const url = import.meta.env.VITE_SUPABASE_URL;
  const key = import.meta.env.VITE_SB_PUBLISHABLE_KEY;
  return url && key ? { supabaseUrl: url, publishableKey: key } : null;
};

let cached: WorkspaceRuntimeConfig | null = null;

/** The already-resolved workspace (App bootstrap ran) - null in the demo. */
export const getResolvedWorkspace = (): WorkspaceRuntimeConfig | null => cached;

/**
 * Resolve this page's workspace config. Order:
 * 1. On a tenant subdomain with a control plane configured: registry lookup
 *    (a 404 is authoritative - the workspace does not exist).
 * 2. Env-var fallback (today's single-tenant deploy; also the safety net if
 *    the control plane is unreachable).
 */
export const resolveWorkspaceConfig = async (): Promise<WorkspaceRuntimeConfig> => {
  if (cached) return cached;

  const controlPlane = import.meta.env.VITE_CONTROL_PLANE_URL;
  const host = parseWorkspaceHost(window.location.hostname);

  if (host.kind === "hq") throw new ReservedHostError("reserved address");
  if (host.kind === "apex") throw new ApexHostError("bare domain");

  if (host.kind === "tenant" && controlPlane) {
    try {
      const res = await fetch(
        `${controlPlane.replace(/\/$/, "")}/w/${host.slug}/config`,
      );
      if (res.status === 404) throw new WorkspaceNotFoundError(host.slug);
      if (res.ok) {
        const body = (await res.json()) as WorkspaceRuntimeConfig;
        if (body.supabaseUrl && body.publishableKey) {
          cached = { ...body, slug: host.slug };
          return cached;
        }
      }
      // Non-404 failure: fall through to the env fallback below.
    } catch (e) {
      if (e instanceof WorkspaceNotFoundError) throw e;
      // Network failure: fall through - better a working fallback than an
      // outage for the single-tenant deployment.
    }
  }

  const env = envConfig();
  if (env) {
    cached = env;
    return cached;
  }
  throw new Error(
    "No workspace configuration: set VITE_SUPABASE_URL + VITE_SB_PUBLISHABLE_KEY, or VITE_CONTROL_PLANE_URL for registry lookup.",
  );
};
